import { useCallback, useEffect, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import OverviewHeader from '../components/overview/overview-header';
import QuestionCard from '../components/overview/question-card';
import { useViewSurveyData } from '../services/useViewSurveyData';
import { useQuestionSummaryActions } from '../services/useQuestionSummaryActions';
import OverviewSidePanel from '../components/overview/overview-sidepanel';
import { surveyHasCreativeAnswers } from '../components/overview/overview-sidepanel-summary';
import { MarketsSection } from '../components/markets/markets-section';
import { FocusGroupSection } from '../components/focus-group/focus-group-section';
import { Skeleton } from 'src/components/ui/skeleton';
import { PortalDsAlert, PortalDsCard, PortalDsEmptyState, portalDs } from 'src/components/portal-design-system';
import { cn } from 'src/lib/utils';
import type { RespondentScope } from '../services/demographic-breakout.helpers';
import { useSurveySummary } from 'src/pages/view-survey/survey-action-plan/useSurveySummary';
import {
  canonicalBreakoutAttribute,
  getSurveyUserBucket,
  listSurveyBreakoutSegments,
} from '../services/demographic-breakout.helpers';
import {
  getAvailableSourceOptions,
  normaliseSourceFilter,
  parseSourceFilter,
  serializeSourceFilter,
} from '../services/source-filter.helpers';
import 'src/redux/survey/prediction-markets.endpoints';
import 'src/redux/survey/focus-group.endpoints';

function parseRespondentScope(value: string | null): RespondentScope {
  if (value === 'ai' || value === 'human') {
    return value;
  }
  return 'all';
}

function parseQuestionIndex(value: string | null, questionCount: number): number | null {
  if (value == null || value === '') {
    return null;
  }
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed) || parsed < 0 || parsed >= questionCount) {
    return null;
  }
  return parsed;
}

const OverviewTab = () => {
  const { extSurveyId } = useParams<{ extSurveyId?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const shareId = searchParams.get('share-id') ?? '';
  const resolvedSurveyId = extSurveyId && extSurveyId !== '' ? extSurveyId : shareId;
  const isShare = Boolean(shareId);

  const {
    isGeneratingSummary,
    isLoadingSummary,
    createSummary,
  } = useSurveySummary(resolvedSurveyId, undefined, isShare);

  const {
    stats,
    survey,
    surveyError,
    surveyId,
    questions,
    summary,
    showSummaries,
  } = useViewSurveyData({ isGeneratingSummary });

  const hasCreativeAnswers = useMemo(() => surveyHasCreativeAnswers(questions), [questions]);

  const {
    canRegenerate,
    isQuestionGenerating,
    regenerateQuestionSummary,
    summaryError,
  } = useQuestionSummaryActions({
    extSurveyId: surveyId,
    isShare,
    surveyStatus: survey?.surveyStatus,
  });

  const breakoutAttribute = canonicalBreakoutAttribute(searchParams.get('breakout'));
  const respondentScope = parseRespondentScope(searchParams.get('respondents'));
  const surveyBreakoutSegments = useMemo(
    () => (breakoutAttribute && stats
      ? listSurveyBreakoutSegments(stats, breakoutAttribute, respondentScope)
      : []),
    [breakoutAttribute, respondentScope, stats]
  );
  const sourceFilterOptions = useMemo(() => getAvailableSourceOptions(stats), [stats]);
  const sourceFilter = normaliseSourceFilter(
    parseSourceFilter(searchParams.get('source')),
    sourceFilterOptions
  );
  const activeQuestionIndex = parseQuestionIndex(searchParams.get('question'), questions.length);
  const isFocusGroupActive = searchParams.has('focusGroup');

  const isLoading = Boolean(surveyId) && !stats && !surveyError;
  const hasQuestions = questions.length > 0;

  const updateSearchParam = useCallback((key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value == null || value === '') {
      next.delete(key);
    } else {
      next.set(key, value);
    }
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleBreakoutChange = useCallback((attributeKey: string | null) => {
    updateSearchParam('breakout', attributeKey);
  }, [updateSearchParam]);

  const handleRespondentScopeChange = useCallback((scope: RespondentScope) => {
    updateSearchParam('respondents', scope === 'all' ? null : scope);
  }, [updateSearchParam]);

  const handleSourceFilterChange = useCallback((source: typeof sourceFilter) => {
    updateSearchParam('source', serializeSourceFilter(source));
  }, [updateSearchParam]);

  const handleQuestionSelect = useCallback((index: number) => {
    updateSearchParam('question', String(index));
    document.getElementById(`overview-question-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [updateSearchParam]);

  const handleFocusGroupSelect = useCallback(() => {
    document.getElementById('overview-focus-group')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useEffect(() => {
    if (activeQuestionIndex == null) {
      return;
    }
    document.getElementById(`overview-question-${activeQuestionIndex}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [activeQuestionIndex]);

  useEffect(() => {
    const parsedSource = parseSourceFilter(searchParams.get('source'));
    const normalised = normaliseSourceFilter(parsedSource, sourceFilterOptions);
    if (parsedSource !== normalised) {
      updateSearchParam('source', serializeSourceFilter(normalised));
    }
  }, [searchParams, sourceFilterOptions, updateSearchParam]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#F4F5F8]">
      <OverviewHeader
        survey={survey}
        stats={stats}
        isShare={isShare}
        breakoutAttribute={breakoutAttribute}
        respondentScope={respondentScope}
        sourceFilter={sourceFilter}
        sourceFilterOptions={sourceFilterOptions}
        onBreakoutChange={handleBreakoutChange}
        onRespondentScopeChange={handleRespondentScopeChange}
        onSourceFilterChange={handleSourceFilterChange}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <div className="min-w-0 flex-1 overflow-y-auto pb-6">
          {isLoading ? (
            <div className="space-y-4 p-6">
              <Skeleton className="h-10 w-2/3 rounded-lg bg-[#EDEEF2]" />
              <Skeleton className="h-64 w-full rounded-xl bg-[#EDEEF2]" />
              <Skeleton className="h-64 w-full rounded-xl bg-[#EDEEF2]" />
            </div>
          ) : null}

          {surveyError ? (
            <div className="m-6">
              <PortalDsAlert
                variant="danger"
                title="Could not load survey results"
              >
                Check that you have access to this survey and try again.
              </PortalDsAlert>
            </div>
          ) : null}

          {!isLoading && !surveyError && !hasQuestions ? (
            <div className="m-6">
              <PortalDsCard>
                <PortalDsEmptyState
                  title="No question results are available yet"
                  description="Results will appear here once respondents have answered this survey."
                />
              </PortalDsCard>
            </div>
          ) : null}

          {!isLoading && !surveyError && hasQuestions ? (
            <div className={cn(portalDs.page, 'gap-4 p-4 md:p-6')}>
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  index={index}
                  question={question}
                  breakoutAttribute={breakoutAttribute}
                  respondentScope={respondentScope}
                  sourceFilter={sourceFilter}
                  surveyBreakoutSegments={surveyBreakoutSegments}
                  isSummaryGenerating={isQuestionGenerating(question.id)}
                  summaryError={summaryError}
                  canRegenerateSummary={canRegenerate}
                  onRegenerateSummary={() => regenerateQuestionSummary(question.id)}
                />
              ))}
            </div>
          ) : null}

          {!isLoading && !surveyError && surveyId && !isShare && stats ? (
            <FocusGroupSection
              extSurveyId={surveyId}
              totalAiRespondents={getSurveyUserBucket(stats, 'ai_users')?.started ?? stats.aiUsers ?? 0}
              questions={questions}
            />
          ) : null}

          {!isLoading && !surveyError && surveyId ? (
            <MarketsSection
              extSurveyId={surveyId}
              share={isShare}
            />
          ) : null}
        </div>

        {stats ? (
          <OverviewSidePanel
            stats={stats}
            breakoutAttribute={breakoutAttribute}
            respondentScope={respondentScope}
            activeQuestionIndex={activeQuestionIndex}
            onQuestionSelect={handleQuestionSelect}
            summary={summary}
            showSummaries={showSummaries}
            surveyStatus={survey?.surveyStatus}
            isShare={isShare}
            isGeneratingSummary={isGeneratingSummary}
            isLoadingSummary={isLoadingSummary}
            hasCreativeAnswers={hasCreativeAnswers}
            onGenerateSummary={createSummary}
            onFocusGroupSelect={!isShare ? handleFocusGroupSelect : undefined}
            isFocusGroupActive={isFocusGroupActive}
          />
        ) : null}
      </div>
    </div>
  );
};

export default OverviewTab;
